/**
 * Per-card variant resolution — attribute fetch, selection narrowing,
 * stock-checked resolve. websitehq owns this outright now (every product
 * card's visuals, everywhere it renders, not just grids it happens to
 * "own" - see grid.js's claimOwnership comment for the older, narrower
 * meaning of "owned" this deliberately does not depend on).
 *
 * Ported from raftor_webhq/public/js/pdp_variant_swatches.js's resolution
 * half (selection → get_next_attribute_and_values → stock-checked
 * get_product_info), reimplemented rather than imported: that file lives in
 * a different app and is DOM-coupled to its own single-swatchContainer page.
 * This is a plain state machine per card, with no DOM of its own — the
 * caller (grid.js) reads its state to update pill classes and to know when
 * a resolved, buyable item_code exists.
 *
 * The three webshop/webhq calls below are read-only, whitelisted DATA APIs
 * (attribute list, next-valid-values, stock) - calling them is the "data
 * lives in webhq" half of the split; nothing here renders or touches a DOM
 * node.
 */

const attributeCache = new Map(); // templateItemCode -> Promise<[{attribute, values}]>

// selectDefault()'s stock-hunt cap — how many values of the first attribute
// it will try before giving up and leaving the card on "Explore".
const MAX_DEFAULT_STOCK_ATTEMPTS = 6;

// Ported verbatim from cart_store.js's own frappeCall: without an error
// handler, a failed request (a genuine backend error on some template, not
// just "no match") never calls back, and this Promise hangs forever - found
// live, cards silently stuck on a half-applied default selection with no
// error surfaced anywhere. The deferred `always` net is the same known
// timing trap that file's own comment documents (fires BEFORE opts.callback,
// so it must be deferred a tick or it would resolve null over every success).
function frappeCall(method, args) {
	return new Promise((resolve) => {
		if (typeof frappe === 'undefined' || !frappe.call) {
			resolve(null);
			return;
		}

		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};

		const req = frappe.call({
			method,
			args,
			callback: (r) => done(r && r.message),
			error: () => done(null),
		});

		if (req && typeof req.always === 'function') {
			req.always(() => setTimeout(() => done(null), 0));
		}
	});
}

function dedupeAttributes(attributes) {
	const seen = new Set();
	return (attributes || []).filter((attr) => {
		if (!attr || seen.has(attr.attribute)) return false;
		seen.add(attr.attribute);
		return true;
	});
}

/** Fetched once per template, shared by every card showing that template. */
function fetchAttributes(templateItemCode) {
	if (!attributeCache.has(templateItemCode)) {
		attributeCache.set(
			templateItemCode,
			frappeCall('webshop.webshop.variant_selector.utils.get_attributes_and_values', {
				item_code: templateItemCode,
			}).then((message) => dedupeAttributes(message))
		);
	}
	return attributeCache.get(templateItemCode);
}

/**
 * Create a resolver for one card. `templateItemCode` is the card's original
 * (template) item_code — never mutated; the resolved variant code is
 * returned to the caller, not written back here.
 */
export function createVariantResolver(templateItemCode) {
	let selection = {};
	let requestId = 0;

	/**
	 * Ask the backend which combinations are valid for `nextSelection`, then
	 * stock-check an exact match. Shared by select() (one attribute changes
	 * at a time) and selectDefault() (the whole selection is set at once) —
	 * same request/response shape either way.
	 */
	async function resolve(nextSelection) {
		selection = nextSelection;
		const id = ++requestId;

		const data = await frappeCall(
			'webshop.webshop.variant_selector.utils.get_next_attribute_and_values',
			{ item_code: templateItemCode, selected_attributes: selection }
		);
		if (id !== requestId) return { status: 'stale' };
		if (!data) return { status: 'no-match' };

		const validOptionsForAttributes = data.valid_options_for_attributes || {};
		const exactMatch = data.exact_match || [];

		if (exactMatch.length !== 1) {
			return { status: 'incomplete', validOptionsForAttributes };
		}

		// Stock-corrected resolve: get_next_attribute_and_values' own
		// available_qty can't be trusted here — same catalog-wide gap
		// pdp_variant_swatches.js's own comment documents (only the
		// template is published as a Website Item, so the resolved
		// variant's own warehouse lookup silently resolves to 0 stock).
		// get_product_info wraps the same core call with the fix.
		const stockMessage = await frappeCall('raftor_webhq.catalog.api.get_product_info', {
			item_code: exactMatch[0],
			skip_quotation_creation: 1,
		});
		if (id !== requestId) return { status: 'stale' };

		const productInfo = (stockMessage && stockMessage.product_info) || {};
		return {
			status: 'resolved',
			itemCode: exactMatch[0],
			inStock: productInfo.can_add_to_cart !== false,
			validOptionsForAttributes,
		};
	}

	return {
		templateItemCode,

		/** [{attribute, values: [...]}], deduped. Cached across cards/instances. */
		getAttributes() {
			return fetchAttributes(templateItemCode);
		},

		getSelection() {
			return Object.assign({}, selection);
		},

		/**
		 * Record a pick and resolve. Resolves to one of:
		 *   { status: 'incomplete', validOptionsForAttributes }
		 *   { status: 'resolved', itemCode, inStock: true }
		 *   { status: 'resolved', itemCode, inStock: false }
		 *   { status: 'no-match' }
		 * A stale response (a newer selection has already superseded this one)
		 * resolves to `{ status: 'stale' }` — the caller should ignore it.
		 */
		select(attribute, value) {
			return resolve(Object.assign({}, selection, { [attribute]: value }));
		},

		/**
		 * Pick the first value of every attribute row and resolve — parity
		 * with the PDP's own applyDefaultPreselection() (pdp_variant_swatches
		 * .js): "the page never sits idle" applies here too, every card with
		 * options should land on a real, addable, IN-STOCK variant without
		 * the shopper having to click first.
		 *
		 * Picks ONE ATTRIBUTE AT A TIME, narrowing with each step, not all
		 * attributes' globally-first values at once in a single request.
		 * Found live: a template's variant matrix is routinely sparse (not
		 * every colour × size combination is a real SKU), so "first colour
		 * AND first size, independently chosen" resolved to zero matches on
		 * most real templates tested — exact_match: [] even though both
		 * values individually existed. Narrowing after each pick (the same
		 * thing a real click does, and the reason the click flow's own
		 * resolve() already works correctly) is what makes this reliably
		 * land on a combination that actually exists.
		 *
		 * If that combination exists but is out of stock, retries with the
		 * NEXT value of the first attribute (Size, in this catalog) before
		 * giving up — found live, the smallest size is very commonly the one
		 * sold out, and a default that always lands on it made almost every
		 * card look unavailable even when other sizes were in stock. Bounded
		 * rather than the PDP script's full combinatorial hunt across every
		 * attribute (MAX_STOCK_SEARCH_COMBINATIONS there) — this only walks
		 * the one attribute smallest-size depletion actually affects, which
		 * covers the common case without that search's added complexity.
		 *
		 * Resolves to the same shape as select(), plus `selection` (the
		 * {attribute: value} picks actually made) so the caller can mark the
		 * matching pills active. `{ status: 'no-attributes' }` when the
		 * template has none to preselect from.
		 */
		async selectDefault() {
			const attributes = await this.getAttributes();
			if (!attributes.length) return { status: 'no-attributes' };

			const [firstAttr, ...restAttrs] = attributes;
			const firstValues = (firstAttr.values || []).slice(0, MAX_DEFAULT_STOCK_ATTEMPTS);

			// Every firstValue this loop tries and finds resolved-but-out-of-
			// stock is a real, load-bearing fact the caller has no other way
			// to learn — the loop below already pays for exactly this check
			// per value, on the hunt for an in-stock one. Discarding it (the
			// old behaviour: return only the last attempt) left the caller
			// with nothing to act on when every attempt failed, so a
			// template that's out of stock in EVERY size rendered its pills
			// as plain and clickable-looking, identical to one that just
			// hasn't been picked yet - the shopper had no way to tell "sold
			// out everywhere" from "nothing selected". Collected regardless
			// of the loop's overall outcome, not just on total failure, so a
			// partial default (some sizes in stock, most not) still lets
			// the caller grey out the ones it already proved are dead ends.
			const outOfStockAttempts = [];

			let result = { status: 'incomplete', validOptionsForAttributes: {} };
			for (const firstValue of firstValues.length ? firstValues : [undefined]) {
				let picked = firstValue !== undefined ? { [firstAttr.attribute]: firstValue } : {};
				result = firstValue !== undefined ? await resolve(picked) : result;
				if (result.status === 'stale' || result.status === 'no-match') {
					return Object.assign({ outOfStockAttempts }, result);
				}

				for (const attr of restAttrs) {
					if (result.status === 'resolved') break; // fully resolved before every attribute was even needed
					const values = attr.values || [];
					if (!values.length) continue;

					const validForThisAttr = result.validOptionsForAttributes && result.validOptionsForAttributes[attr.attribute];
					const value = (validForThisAttr && validForThisAttr[0]) || values[0];

					picked = Object.assign({}, picked, { [attr.attribute]: value });
					result = await resolve(picked);
					if (result.status === 'stale' || result.status === 'no-match') {
						return Object.assign({ selection: picked, outOfStockAttempts }, result);
					}
				}

				if (result.status === 'resolved' && result.inStock) {
					return Object.assign({ selection: picked, outOfStockAttempts }, result);
				}
				if (result.status === 'resolved' && !result.inStock && firstValue !== undefined) {
					outOfStockAttempts.push({ attribute: firstAttr.attribute, value: firstValue });
				}
				// Not in stock (or never reached a single match) — loop to the
				// next candidate for the first attribute, same picked shape
				// reused as the return value if every attempt is exhausted.
				result = Object.assign({ selection: picked }, result);
			}

			// True only when every value of the first attribute was actually
			// tried (not just up to MAX_DEFAULT_STOCK_ATTEMPTS) and every one
			// resolved out of stock - the one case the caller can honestly
			// call the whole template sold out, as opposed to "the search cap
			// gave up before finding one" (a real distinction on a template
			// with more sizes than the cap covers, where an in-stock value
			// past the 6th may still exist and this must not claim otherwise).
			const soldOutEverywhere =
				(firstAttr.values || []).length === firstValues.length &&
				outOfStockAttempts.length === firstValues.length;

			return Object.assign({ outOfStockAttempts, soldOutEverywhere }, result);
		},

		/** Deselect everything (e.g. the card scrolled out and back into view). */
		reset() {
			selection = {};
			requestId += 1; // invalidate any in-flight request
		},

		/**
		 * Bulk, stock-checked values of one attribute for the size-dropdown
		 * presentation, tied to an already-resolved partial selection (e.g.
		 * {Colour: 'Black'}) — one round trip, not one per candidate size (the
		 * exact N-call pattern grid.js's DEFAULT_SELECTION_CONCURRENCY throttle
		 * already exists to keep out of the default-selection burst).
		 *
		 * Resolves to `null` on a failed call (frappeCall's own contract - the
		 * caller must not treat that the same as a real empty result) or the
		 * array of in-stock values otherwise (possibly empty, meaning this
		 * selection is genuinely sold out in every value of targetAttribute).
		 */
		getStockedValues(targetAttribute, selectedAttributes) {
			return frappeCall('raftor_webhq.catalog.api.get_in_stock_attribute_values', {
				template_item_code: templateItemCode,
				selected_attributes: selectedAttributes,
				target_attribute: targetAttribute,
			}).then((message) => (message ? message.values || [] : null));
		},
	};
}
